import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { SPELL_LEVELS } from '../../lib/spellSchools'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './AdminEditor.css'

const EMPTY_SPELL = {
  name: '',
  level: 0,
  school: '',
  officialEquivalent: '',
  castingTime: '1 action',
  range: 'Self',
  components: {
    verbal: true,
    somatic: false,
    material: false,
    materialDescription: '',
  },
  duration: 'Instantaneous',
  concentration: false,
  description: '',
  atHigherLevels: '',
  grantedCharacterIds: [],
  hidden: false,
}

// Official D&D 5e schools for in-game equivalent selection
const OFFICIAL_SCHOOLS = [
  { slug: 'abjuration', name: 'Abjuration' },
  { slug: 'conjuration', name: 'Conjuration' },
  { slug: 'divination', name: 'Divination' },
  { slug: 'enchantment', name: 'Enchantment' },
  { slug: 'evocation', name: 'Evocation' },
  { slug: 'illusion', name: 'Illusion' },
  { slug: 'necromancy', name: 'Necromancy' },
  { slug: 'transmutation', name: 'Transmutation' },
]

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
}

export default function SpellEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingSpell, loading } = useDocument('spells', isNew ? null : id)
  const { data: characters } = useCollection('characters', { orderByField: 'name' })
  const { data: spellSchools, loading: schoolsLoading } = useCollection('spellSchools', { orderByField: 'sortOrder' })
  const { add, update, remove } = useFirestoreMutations('spells')

  const [spell, setSpell] = useState(EMPTY_SPELL)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingSpell) {
      setSpell({
        ...EMPTY_SPELL,
        ...existingSpell,
        components: {
          ...EMPTY_SPELL.components,
          ...existingSpell.components,
        },
      })
    }
  }, [existingSpell])

  const handleChange = (field, value) => {
    setSpell(prev => ({ ...prev, [field]: value }))
  }

  const handleComponentChange = (field, value) => {
    setSpell(prev => ({
      ...prev,
      components: { ...prev.components, [field]: value },
    }))
  }

  const handleCharacterToggle = (charId) => {
    setSpell(prev => {
      const currentIds = prev.grantedCharacterIds || []
      if (currentIds.includes(charId)) {
        return { ...prev, grantedCharacterIds: currentIds.filter(id => id !== charId) }
      } else {
        return { ...prev, grantedCharacterIds: [...currentIds, charId] }
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!spell.name.trim()) {
      setError('Spell name is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isNew) {
        await add(spell)
      } else {
        await update(id, spell)
      }
      navigate('/admin/spells')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${spell.name}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/spells')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  // Filter to show only PCs for granting spells
  const pcCharacters = characters.filter(c => c.type === 'pc')

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Spell' : 'Edit Spell'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/spells')}
        >
          &larr; Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        {/* Basic Info */}
        <fieldset className="form-section">
          <legend>Basic Information</legend>

          <div className="form-group">
            <label htmlFor="name">Spell Name *</label>
            <input
              id="name"
              type="text"
              value={spell.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Fireball"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="level">Spell Level</label>
              <select
                id="level"
                value={spell.level}
                onChange={(e) => handleChange('level', parseInt(e.target.value))}
              >
                {SPELL_LEVELS.map(lvl => (
                  <option key={lvl.value} value={lvl.value}>
                    {lvl.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="school">School</label>
              {schoolsLoading ? (
                <select disabled>
                  <option>Loading...</option>
                </select>
              ) : spellSchools.length === 0 ? (
                <div>
                  <select disabled>
                    <option>No schools available</option>
                  </select>
                  <p className="help-text">
                    <Link to="/admin/spell-schools">Create spell schools</Link> first.
                  </p>
                </div>
              ) : (
                <select
                  id="school"
                  value={spell.school}
                  onChange={(e) => handleChange('school', e.target.value)}
                >
                  <option value="">Select a school...</option>
                  {spellSchools.map(school => (
                    <option key={school.id} value={school.slug}>
                      {school.icon} {school.name}
                      {school.unofficial ? ' (Unofficial)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Show official equivalent dropdown when an unofficial school is selected */}
          {(() => {
            const selectedSchool = spellSchools.find(s => s.slug === spell.school)
            if (selectedSchool?.unofficial) {
              return (
                <div className="form-group">
                  <label htmlFor="officialEquivalent">Official Equivalent (In-Game)</label>
                  <select
                    id="officialEquivalent"
                    value={spell.officialEquivalent}
                    onChange={(e) => handleChange('officialEquivalent', e.target.value)}
                  >
                    <option value="">— None —</option>
                    {OFFICIAL_SCHOOLS.map(s => (
                      <option key={s.slug} value={s.slug}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="help-text">
                    The official school this spell maps to for game mechanics (e.g., Abjurer's Arcane Ward)
                  </p>
                </div>
              )
            }
            return null
          })()}
        </fieldset>

        {/* Casting Info */}
        <fieldset className="form-section">
          <legend>Casting</legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="castingTime">Casting Time</label>
              <input
                id="castingTime"
                type="text"
                value={spell.castingTime}
                onChange={(e) => handleChange('castingTime', e.target.value)}
                placeholder="1 action"
              />
            </div>

            <div className="form-group">
              <label htmlFor="range">Range</label>
              <input
                id="range"
                type="text"
                value={spell.range}
                onChange={(e) => handleChange('range', e.target.value)}
                placeholder="120 feet"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="duration">Duration</label>
              <input
                id="duration"
                type="text"
                value={spell.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="Instantaneous"
              />
            </div>

            <div className="form-group form-group-inline">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={spell.concentration === true}
                  onChange={(e) => handleChange('concentration', e.target.checked)}
                />
                <span>Concentration</span>
              </label>
            </div>
          </div>
        </fieldset>

        {/* Components */}
        <fieldset className="form-section">
          <legend>Components</legend>

          <div className="form-group">
            <div style={{ display: 'flex', gap: 'var(--space-lg)', marginBottom: 'var(--space-md)' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={spell.components.verbal === true}
                  onChange={(e) => handleComponentChange('verbal', e.target.checked)}
                />
                <span>Verbal (V)</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={spell.components.somatic === true}
                  onChange={(e) => handleComponentChange('somatic', e.target.checked)}
                />
                <span>Somatic (S)</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={spell.components.material === true}
                  onChange={(e) => handleComponentChange('material', e.target.checked)}
                />
                <span>Material (M)</span>
              </label>
            </div>

            {spell.components.material && (
              <div className="form-group">
                <label htmlFor="materialDescription">Material Components</label>
                <input
                  id="materialDescription"
                  type="text"
                  value={spell.components.materialDescription}
                  onChange={(e) => handleComponentChange('materialDescription', e.target.value)}
                  placeholder="a tiny ball of bat guano and sulfur"
                />
              </div>
            )}
          </div>
        </fieldset>

        {/* Description */}
        <fieldset className="form-section">
          <legend>Description</legend>

          <div className="form-group">
            <label>Spell Description *</label>
            <div className="rich-text-editor">
              <ReactQuill
                theme="snow"
                value={spell.description}
                onChange={(value) => handleChange('description', value)}
                modules={QUILL_MODULES}
                placeholder="Describe the spell's effects..."
              />
            </div>
          </div>

          <div className="form-group">
            <label>At Higher Levels</label>
            <p className="help-text" style={{ marginTop: 0, marginBottom: 'var(--space-sm)' }}>
              Optional. Describe how the spell scales when cast with a higher-level slot.
            </p>
            <div className="rich-text-editor">
              <ReactQuill
                theme="snow"
                value={spell.atHigherLevels}
                onChange={(value) => handleChange('atHigherLevels', value)}
                modules={QUILL_MODULES}
                placeholder="When you cast this spell using a spell slot of 4th level or higher..."
              />
            </div>
          </div>
        </fieldset>

        {/* Granted Characters */}
        <fieldset className="form-section">
          <legend>Grant to Characters</legend>
          <p className="help-text" style={{ marginTop: 0, marginBottom: 'var(--space-md)' }}>
            Select which characters have access to this spell in their spellbook.
          </p>

          <div className="character-checkboxes">
            {pcCharacters.map(char => (
              <label key={char.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(spell.grantedCharacterIds || []).includes(char.id)}
                  onChange={() => handleCharacterToggle(char.id)}
                />
                <span>
                  {char.name}
                  {char.active === false && <span className="inactive-tag">Inactive</span>}
                </span>
              </label>
            ))}
            {pcCharacters.length === 0 && (
              <p className="text-secondary">No player characters available</p>
            )}
          </div>
        </fieldset>

        {/* Visibility */}
        <div className="form-group form-group-inline">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={spell.hidden === true}
              onChange={(e) => handleChange('hidden', e.target.checked)}
            />
            <span>Hidden (Spoiler)</span>
          </label>
          <span className="help-text">Only visible in admin until revealed</span>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Spell' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Spell
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
